/**
 * Presentation helpers for the per-member run resolution of an analysis group (M4 contract 5)
 * and the anti-survivorship buckets of the group metrics (contract 6). Pure functions so the
 * members table, the evaluation panel and the list chips share one vocabulary.
 *
 * A `RunResolution` is `{ bucket, run_id, status, degraded, not_requested, engine_versions }`
 * where bucket is one of: 'pinned' | 'ok' | 'pinned_missing' | 'pending' | 'not_analysed' |
 * 'never_run'.
 */

export const RESOLUTION_BUCKETS = Object.freeze([
  'pinned',
  'ok',
  'pinned_missing',
  'pending',
  'not_analysed',
  'never_run',
]);

/** Buckets whose run counts in the metrics (a resolved SUCCEEDED run of the group). */
export const EVALUABLE_BUCKETS = Object.freeze(['ok', 'pinned']);

/** Buckets reported together as "Not analysed" (missed for incoherents, never a false alarm). */
export const NOT_ANALYSED_BUCKETS = Object.freeze([
  'pending',
  'not_analysed',
  'never_run',
  'pinned_missing',
]);

const BUCKET_PRESENTATION = Object.freeze({
  pinned: { label: 'Pinned', color: 'info', icon: 'mdi-pin', evaluable: true },
  ok: { label: 'Analysed', color: 'success', icon: 'mdi-check-circle-outline', evaluable: true },
  pinned_missing: {
    label: 'Pinned run missing',
    color: 'error',
    icon: 'mdi-pin-off-outline',
    evaluable: false,
  },
  pending: { label: 'Pending', color: 'primary', icon: 'mdi-progress-clock', evaluable: false },
  not_analysed: {
    label: 'Not analysed',
    color: 'warning',
    icon: 'mdi-alert-circle-outline',
    evaluable: false,
  },
  never_run: {
    label: 'Never run',
    color: 'grey',
    icon: 'mdi-minus-circle-outline',
    evaluable: false,
  },
});

export function bucketOf(resolution) {
  if (!resolution) return 'never_run';
  const bucket = typeof resolution === 'string' ? resolution : resolution.bucket;
  return RESOLUTION_BUCKETS.includes(bucket) ? bucket : 'never_run';
}

/** `{ label, color, icon, evaluable }` for a bucket or a resolution object. */
export function bucketPresentation(resolution) {
  return BUCKET_PRESENTATION[bucketOf(resolution)];
}

export function isEvaluable(resolution) {
  return EVALUABLE_BUCKETS.includes(bucketOf(resolution));
}

/**
 * Chips to render next to a member: the bucket chip plus 'Degraded' / 'Not requested' flags.
 * @returns {Array<{ key: string, text: string, color: string, icon: string, outlined: boolean }>}
 */
export function resolutionChips(resolution) {
  const bucket = bucketOf(resolution);
  const base = BUCKET_PRESENTATION[bucket];
  const chips = [
    { key: bucket, text: base.label, color: base.color, icon: base.icon, outlined: false },
  ];
  if (resolution && typeof resolution === 'object') {
    if (resolution.degraded) {
      chips.push({
        key: 'degraded',
        text: 'Degraded',
        color: 'error',
        icon: 'mdi-alert-decagram-outline',
        outlined: true,
      });
    }
    if (resolution.not_requested) {
      chips.push({
        key: 'not_requested',
        text: 'Not requested',
        color: 'grey',
        icon: 'mdi-power-plug-off',
        outlined: true,
      });
    }
  }
  return chips;
}

/**
 * Counts per bucket plus the derived totals used by the group cards and tabs.
 * @param {Array<{ resolved?: RunResolution }>} members
 * @returns {{ total: number, evaluated: number, not_analysed: number, pending: number,
 *   degraded: number, not_requested: number, by_bucket: Record<string, number> }}
 */
export function summarizeResolutions(members) {
  const list = Array.isArray(members) ? members : [];
  const byBucket = {};
  RESOLUTION_BUCKETS.forEach((bucket) => {
    byBucket[bucket] = 0;
  });
  let degraded = 0;
  let notRequested = 0;
  list.forEach((member) => {
    const resolution = member && member.resolved ? member.resolved : member;
    byBucket[bucketOf(resolution)] += 1;
    if (resolution && resolution.degraded) degraded += 1;
    if (resolution && resolution.not_requested) notRequested += 1;
  });
  return {
    total: list.length,
    evaluated: EVALUABLE_BUCKETS.reduce((sum, bucket) => sum + byBucket[bucket], 0),
    not_analysed: NOT_ANALYSED_BUCKETS.reduce((sum, bucket) => sum + byBucket[bucket], 0),
    pending: byBucket.pending,
    degraded,
    not_requested: notRequested,
    by_bucket: byBucket,
  };
}

/** Bucket of a per-video metrics row (`bucket` from the group scope, legacy fields otherwise). */
export function rowBucket(row) {
  if (!row) return 'never_run';
  if (row.bucket && RESOLUTION_BUCKETS.includes(row.bucket)) return row.bucket;
  if (row.coherence_status === 'not_requested') return 'ok';
  return row.analysis_available === false ? 'never_run' : 'ok';
}

export function rowIsNotRequested(row) {
  return Boolean(
    row &&
      (row.not_requested === true ||
        row.coherence_status === 'not_requested' ||
        row.bucket === 'not_requested')
  );
}

export function rowIsDegraded(row) {
  return Boolean(row && (row.degraded === true || (row.engine && row.engine.degraded)));
}

/**
 * Splits the per-video rows of a metrics payload into the three listed buckets of contract 6.
 * A row belongs to exactly one list: not requested wins over degraded, which wins over the
 * not-analysed buckets (a pending video cannot be degraded yet).
 * @param {Array<object>} rows `byVideo` rows (each with `bucket`, `degraded`, `coherence_status`)
 * @returns {{ not_analysed: object[], degraded: object[], not_requested: object[] }}
 */
export function bucketLists(rows) {
  const lists = { not_analysed: [], degraded: [], not_requested: [] };
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (rowIsNotRequested(row)) lists.not_requested.push(row);
    else if (rowIsDegraded(row)) lists.degraded.push(row);
    else if (NOT_ANALYSED_BUCKETS.includes(rowBucket(row))) lists.not_analysed.push(row);
  });
  return lists;
}

/**
 * Route to open a member or metrics row, with `?run=` when a resolved run is known.
 * @param {{ source_type: string, source_id: number|string }} item
 * @param {string|null} runId
 * @returns {string|null}
 */
export function sourceRouteWithRun(item, runId = null) {
  if (!item || item.source_id == null) return null;
  const base =
    item.source_type === 'interview'
      ? `/interview-analysis/${encodeURIComponent(item.source_id)}`
      : `/coherence-analysis/${encodeURIComponent(item.source_id)}`;
  const run = runId ?? item.run_id ?? (item.resolved && item.resolved.run_id) ?? null;
  return run ? `${base}?run=${encodeURIComponent(run)}` : base;
}

/** 'Interview #9' / 'Video #17' fallback when the server did not provide a name. */
export function memberLabel(member) {
  if (!member) return '';
  if (member.name) return String(member.name);
  if (member.video_name) return String(member.video_name);
  const noun = member.source_type === 'interview' ? 'Interview' : 'Video';
  return `${noun} #${member.source_id}`;
}

/** 'gestures 2.0.3 · coherence 1.4.2' from an engine_versions map (highlighted engines first). */
export function formatEngineVersions(versions, { max = 2 } = {}) {
  if (!versions || typeof versions !== 'object') return '';
  const order = ['gestures', 'coherence'];
  const names = [
    ...order.filter((name) => versions[name] != null),
    ...Object.keys(versions)
      .filter((name) => !order.includes(name) && versions[name] != null)
      .sort(),
  ];
  const shown = names.slice(0, max).map((name) => `${name} ${versions[name]}`);
  const rest = names.length - shown.length;
  if (rest > 0) shown.push(`+${rest} engines`);
  return shown.join(' · ');
}

/** Default batch name for a group run, mirroring the server: '<group name>@<yyyy-mm-dd HH:mm>'. */
export function defaultGroupBatch(groupName, date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
  return `${groupName || 'group'}@${stamp}`;
}
